<script setup lang="ts">
import AppButton from "@/components/atoms/button/AppButton.vue";
import FormSelectGroup from "@/components/molecules/form/FormSelectGroup.vue";
import WorkTypeRadioGroup from "@/components/molecules/orders/WorkTypeRadioGroup.vue";
import { ref, computed, watch, onBeforeMount } from "vue";
import { CustomFormOption } from "@/types/form/FormOption";
import { localitiesService } from "@/app/services/LocalitiesService";
import { getFcmToken } from "@/utils/firebase";
import { accountService } from "@/app/services/AccountService";
import { authStore } from "@/store/auth";
import { useLoader } from "@/utils/loader";
import { getLocationPosition } from "@/utils/googlemap";
import { captureException } from "@/plugins/sentry";
import { masterStore } from "@/store/master";
import { Prefecture } from "@/types/Master";

type InputValue = {
  prefecture: number | null;
  locality: number | null;
  workerServiceId: number | null;
};

defineProps({
  show: {
    type: Boolean,
    required: true,
    default: false
  }
});
const emit = defineEmits(["close"]);

const loader = useLoader();
const prefectures = ref<Prefecture[]>([]);
const localities = ref<CustomFormOption<number>[]>([]);
const inputValue = ref<InputValue>({
  prefecture: null,
  locality: null,
  workerServiceId: null
});

const prefectureOptions = computed(() => {
  return prefectures.value.map(prefecture => {
    return {
      value: prefecture.id,
      text: prefecture.prefecture
    };
  });
});

const localityOptions = computed(() =>
  localities.value.map(locality => ({
    value: locality.value,
    text: locality.text
  }))
);

const workerServiceOptions = computed(
  () => authStore.workerServiceOptionsByProduct
);

const isShownWorkerService = computed(
  // 複数の業務カテゴリがある場合のみ選択肢を表示する
  () => workerServiceOptions.value.length >= 2
);

watch(
  () => inputValue.value.prefecture,
  async (newValue, oldValue) => {
    if (newValue === null || newValue === oldValue) return;
    const res = await localitiesService.fetchLocalitiesWithSeirei(newValue);
    localities.value = res
      // 政令指定都市は選択肢に表示しない
      .filter(l => !l.isSeirei)
      .map(locality => {
        return {
          value: locality.id,
          text: locality.city
        };
      });
    inputValue.value.locality = null;
  }
);

const onClickOk = async () => {
  loader.show();

  let fcmToken = null;
  try {
    fcmToken = await getFcmToken();
  } catch (e) {
    captureException(e as Error);
  }

  let longitude = null;
  let latitude = null;
  try {
    const locality = localityOptions.value.find(
      l => l.value === inputValue.value.locality!
    );
    if (locality) {
      const location = await getLocationPosition(locality.text);
      longitude = Math.round(location.longitude * 1e6) / 1e6;
      latitude = Math.round(location.latitude * 1e6) / 1e6;
    }
  } catch (e) {
    captureException(e as Error);
  }

  // 現在のプロダクトに紐づく業務カテゴリが1つしかない場合は、業務カテゴリは表示されていないので、その1つを設定する
  const workerServiceId = isShownWorkerService.value
    ? inputValue.value.workerServiceId!
    : workerServiceOptions.value[0].value;

  try {
    const firstBootSurvey = await accountService.registerFirstBootSurveyV2(
      fcmToken,
      workerServiceId,
      inputValue.value.prefecture!,
      inputValue.value.locality!,
      longitude,
      latitude
    );
    authStore.setFirstBootSurveyV2(firstBootSurvey);
    emit("close");
  } catch (e) {
    captureException(e as Error);
  }
  loader.hide();
};

onBeforeMount(() => {
  prefectures.value = masterStore.prefectures;
});
</script>

<template>
  <BModal
    :modelValue="show"
    no-close-on-backdrop
    content-class="first-boot-modal-content"
    body-class="first-boot-modal-body"
    no-close-on-esc
    hide-header
    hide-footer
    centered
  >
    <div class="header">
      <div class="header-content">
        <div class="ribbon">
          <p class="ribbon-title">カイテクへようこそ</p>
        </div>
        <div class="header-detail">
          <img src="@/assets/svg/firstBootSurvey/caikuma_06_reverse.svg?webp" />
          <div class="header-text-wrapper">
            <p class="header-main-text">
              最適なお仕事を表示するために選択をお願いします
            </p>
            <p class="header-sub-text">
              この設定はあとから変更できます
            </p>
          </div>
        </div>
      </div>
    </div>
    <CustomValidationObserver
      tag="div"
      class="body"
      v-slot="{ handleSubmit }"
      :initialValues="inputValue"
      ref="vObserver"
    >
      <div class="question">
        <p>
          <span class="question-prefix">Q.</span>
          <span class="question-title">お住まいの地域は？</span>
        </p>
        <div
          class="question-answer question-answer-margin-right question-answer-gap"
        >
          <div>
            <ValidationField
              v-slot="validationState"
              label="都道府県"
              rules="required"
              name="prefecture"
              v-model="inputValue.prefecture"
            >
              <FormSelectGroup
                firstOption="都道府県を選択"
                class="prefecture-select"
                v-model="inputValue.prefecture"
                :options="prefectureOptions"
                :validation-state="validationState"
              />
            </ValidationField>
          </div>
          <div>
            <ValidationField
              v-slot="validationState"
              label="市区町村"
              tag="div"
              rules="required"
              name="locality"
              v-model="inputValue.locality"
            >
              <FormSelectGroup
                firstOption="市区町村を選択"
                class="locality-select"
                v-model="inputValue.locality"
                :options="localityOptions"
                :validation-state="validationState"
              />
            </ValidationField>
          </div>
        </div>
      </div>
      <div class="question" v-if="isShownWorkerService">
        <p>
          <span class="question-prefix">Q.</span>
          <span class="question-title">お探しの業務は？</span>
        </p>
        <div class="question-answer">
          <ValidationField
            v-slot="validationState"
            label="業務"
            name="workerServiceId"
            rules="required"
            v-model="inputValue.workerServiceId"
          >
            <WorkTypeRadioGroup
              v-model:value="inputValue.workerServiceId"
              :validationState="validationState"
              name="workerServiceId"
              :options="workerServiceOptions"
              class="radios"
            />
          </ValidationField>
        </div>
      </div>
      <div class="buttons">
        <AppButton @click="handleSubmit(onClickOk)">確定する</AppButton>
      </div>
    </CustomValidationObserver>
  </BModal>
</template>

<style lang="scss" scoped>
:global(.first-boot-modal-body) {
  padding: 0;
  font-family: "Noto Sans JP", sans-serif;
}

:global(.first-boot-modal-content) {
  border: none;
}

.first-boot-modal-body {
  & .header {
    background-color: rgb(255 248 225 / 100%);
    background-image: repeating-linear-gradient(
      -45deg,
      transparent,
      transparent 21px,
      rgba(251 206 90 / 20%) 21px,
      rgba(251 206 90 / 20%) 42px
    );
    border-top: 6px solid #fbce5a;
    border-bottom: 6px solid #fbce5a;
    border-radius: 6px 6px 0 0;

    & .header-content {
      margin: 0 16px 10px;
    }

    & .header-detail {
      display: flex;
      flex-direction: row;
      margin-top: 10px;

      & .header-detail-margin-top {
        margin-top: 14px;
      }
    }

    & .header-text-wrapper {
      display: flex;
      flex-direction: column;
      gap: 4px;
      justify-content: center;
      margin-left: 12px;
    }

    & .header-main-text {
      font-size: 16px;
      font-weight: 400;
      line-height: 24px;
      text-align: left;
    }

    & .header-sub-text {
      font-size: 12px;
      font-weight: 400;
      line-height: 18px;
      text-align: left;
    }
  }

  & .ribbon {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 40px;
    margin-top: 10px;
    background: url("@/assets/svg/firstBootSurvey/bg_ribbon.svg") no-repeat
      center center;

    & .ribbon-title {
      font-size: 19px;
      font-weight: 700;
      color: #fff;
      letter-spacing: 0.12em;
    }
  }

  & .body {
    display: flex;
    flex-direction: column;
    gap: 24px;
    margin: 16px 16px 24px;

    & .body-title {
      font-size: 16px;
      font-weight: 600;
      line-height: 24px;
      color: #000c;
      text-align: left;

      &.body-title-margin-top {
        margin-top: 12px;
      }
    }

    & .buttons {
      display: flex;
      flex-direction: column;
      gap: 16px;
      align-items: center;

      &:deep(.app-button) {
        width: 100%;
        font-size: 16px;
        font-weight: 500;
        line-height: 21.79px;
        text-align: center;
        background: #3f88ae;
        border: none;
        border-radius: 4px;
      }

      & .buttons-register {
        width: 100%;
      }

      & .buttons-search {
        font-size: 14px;
        font-weight: 400;
        line-height: 21px;
        color: #3f88ae;
      }
    }
  }

  & .question {
    & .question-prefix {
      font-size: 20px;
      font-weight: 600;
      line-height: 20px;
      color: #fbce5a;
      text-align: left;
    }

    & .question-title {
      margin-left: 6px;
      font-size: 16px;
      font-weight: 400;
      line-height: 24px;
      text-align: left;
    }

    & .question-answer {
      display: flex;
      flex-direction: column;
      margin-top: 12px;

      &.question-answer-margin-right {
        margin-right: 18px;
      }

      &.question-answer-gap {
        gap: 8px;
      }
    }

    & .prefecture-select,
    .locality-select {
      & :deep(.app-select) {
        padding: 0 8px 0 12px;
        background-image: url("@/assets/svg/firstBootSurvey/prefecture_locality_select_arrow.svg");
        background-position: right 2px center;
        background-size: 24px;
      }
    }

    & .locality-select {
      margin-top: -0.25rem;
    }
  }
}
</style>
